import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VERSION } from '../../app.constants';
import { JhiLanguageService } from '../../shared/core-utils';
import { LoginService } from '../../shared/login/login.service';
import { LoginPageDetails } from '../../shared/login/login.model';
import { LoginContext } from '../../shared/login/login-context';
import { Subscription } from 'rxjs';

@Component({
    selector: 'jhi-one-time-password',
    templateUrl: './one-time-password.component.html',
    styleUrls: [
        '../login.component.scss'
    ],
    encapsulation: ViewEncapsulation.None
})
export class OneTimePasswordComponent implements OnInit, OnDestroy {

    version = VERSION;
    pageDetails: LoginPageDetails;
    otpCode: string;

    formInvalid = 'login-form-invalid';
    timeRemaining = this.context.pageDetails.otpExpiryIntervalInSeconds;
    enableResendLink = false;
    timer: any;

    display_name: string;
    return_url: string;
    client_id: string;
    redirect_uri: string;
    code_challenge: string;
    code_challenge_method: string;
    state: string;

    isFormValid = true;
    isLoading = false;
    errorMessage: string;
    errorFields = [];

    private queryParamSubscription: Subscription;

    constructor(private context: LoginContext,
                private route: ActivatedRoute,
                private languageService: JhiLanguageService,
                private loginService: LoginService,
                private router: Router
    ) {
        document.body.classList.add('body-container');
        this.queryParamSubscription = this.route.queryParams.subscribe((params) => {
            this.display_name = params['display_name'];
            this.return_url = params['return_url'];
            this.client_id = params['client_id'];
            this.redirect_uri = params['redirect_uri'];
            this.code_challenge = params['code_challenge'];
            this.code_challenge_method = params['code_challenge_method'];
            this.state = params['state'];
        });
    }

    ngOnInit(): void {
        this.pageDetails = this.context.pageDetails;
        this.startResendTimer();
    }

    startResendTimer() {
        this.timer = setInterval(() => {
            this.timeRemaining--;

            if (this.timeRemaining === 0) {
                this.enableResendLink = true;
                clearInterval(this.timer);
            }
        }, 1000);
    }

    doVerifyOTP() {
        const request = {
            username: this.context.model.username,
            password: this.context.model.password,
            otpCode: this.otpCode
        };
        this.loginService.verifyOTP(request).toPromise().then((response) => {
            this.processLoginSuccess(response);
        }).catch((response) => {
            this.applyValidationErrors(response ? response.error.errors : {});
        });
    }

    ngOnDestroy(): void {
        document.body.classList.remove('body-container');
        this.queryParamSubscription.unsubscribe();
    }

    async processLoginSuccess(tokenData) {

        // Manually renew session in Fieldbook and GDMS after successful authentication to avoid session fixation
        try {
            await this.loginService.renewFieldbookSession().toPromise();
        } catch (error) {
            this.displayClientError('System failed to renew session. Please contact your system administrator.');
            return;
        }

        // Aside from generating token from validateLogin,
        // We need to initialize Spring Security authentication for Workbench
        this.loginService.authorizeWorkbench(this.context.model).toPromise().then((request) => {
            // This service will always return successful since we already
            // authenticated the user via validateLogin
            const validatedReturnUrl = this.getValidatedReturnUrl(this.return_url);
            if (this.display_name && this.return_url) {
                if (validatedReturnUrl) {
                    window.top.location.href = validatedReturnUrl;
                } else {
                    this.displayClientError('Invalid return URL.');
                }
            } else if (this.client_id && this.redirect_uri) {
                this.router.navigate(['/authorize-login'], {
                    queryParams: {
                        display_name: this.display_name,
                        return_url: this.return_url,
                        client_id: this.client_id,
                        redirect_uri: this.redirect_uri,
                        code_challenge: this.code_challenge,
                        code_challenge_method: this.code_challenge_method,
                        state: this.state
                    }
                });
            } else {
                this.context.recentlyLogin = true;
                this.router.navigate(['']);
            }
        });

    }

    private getValidatedReturnUrl(returnUrl: string): string | null {
        if (!returnUrl) {
            return null;
        }

        try {
            const validatedUrl = new URL(returnUrl, window.location.origin);
            return validatedUrl.origin === window.location.origin ? validatedUrl.href : null;
        } catch (error) {
            return null;
        }
    }

    doCreateOTP() {
        const request = {
            username: this.context.model.username,
            password: this.context.model.password
        };

        this.isLoading = true;
        this.loginService.createOTP(request).toPromise().then((response) => {
            this.isLoading = false;
            this.timeRemaining = this.context.pageDetails.otpExpiryIntervalInSeconds;
            this.enableResendLink = false;
            clearInterval(this.timer);
            this.startResendTimer();
        }).catch((response) => {
            this.applyValidationErrors(response ? response.error.errors : {});
        });
    }

    private displayClientError(errorMsg) {
        this.errorMessage = errorMsg;
        this.isFormValid = false;
        this.isLoading = false;
    }

    private applyValidationErrors(errors) {
        let errorMessage = '';

        Object.keys(errors).forEach((fieldName) => {
            this.errorFields.push(fieldName);
            errorMessage += errorMessage ? (' ' + errors[fieldName]) : errors[fieldName];

        });
        this.displayClientError(errorMessage);
    }

    private clearErrors() {
        // Double check if form is actually invalid, as this function may occasionally be called unnecessarily
        if (!this.isFormValid) {
            // Clear client errors
            this.errorMessage = '';
            this.isFormValid = true;
            this.errorFields = [];
        }
    }
}
